import type { RawPlantsResponse, RawProfileResponse } from '@heviton/heviton-api-app-legacy';
import type { RawPlantNode } from '@heviton/heviton-api-v2';
import type {
  AnalysisPlant,
  Plant,
  PlantProfile,
  PlantStatus,
  PlantWithDevices,
} from '@/shared/data-types';
import { toSubscriptionStatus } from './subscription-status';

// v3 발전소 status 코드 → FE 상태(선택 시트 상태점·정렬). dto 기준(101/201/301/401/402). 미지정/미지원은
// 상태 미상(undefined) — 정상으로 숨기지 않음. 402=운영준비(선택 시트에서 비활성). 코드 의미는 BE dto 기준.
const PLANT_STATUS_MAP: Record<string, PlantStatus> = {
  '101': 'normal',
  '201': 'warning',
  '301': 'critical',
  '401': 'commLost',
  '402': 'preparing',
};

// BE `RawPlantNode`(v2) → FE `Plant`(발전소 기본 정보). **순수 변환** — 검증·throw 없음.
// 목록(구독 합성 행)·단건(plan) 모두 이 기본 변환을 공유한다.
// rtu/inverter 는 단건 조회만 제공(목록은 미포함) → 미제공 시 0 폴백.
export function fromRawPlant(raw: RawPlantNode): Plant {
  return {
    plantId: raw.plantId,
    plantName: raw.plantName,
    plantLocation: raw.location,
    plantCapacityKwp: raw.volume,
    plantRtuCount: raw.rtuCount ?? 0,
    plantInverterCount: raw.inverterCount ?? 0,
  };
}

// app-legacy(v3) `GET /plants` → 알림·인버터 필터의 발전소→설비 cascade 목록. inverters·rtus 는 응답에
// 기본 포함(include 불필요 — RN 앱 정합). `instanceId` = v3 이벤트 질의 키(facility), `rtuId` 는 instanceId
// 동반 질의 시 BE 계층검증에 필요(발전소당 RTU 1개 가정 → 첫 RTU). 미제공 필드는 빈 문자열 폴백.
export function mapPlantsWithDevices(raw: RawPlantsResponse): PlantWithDevices[] {
  return raw.plants.map((plant) => ({
    plantId: plant.plantId,
    plantName: plant.name,
    rtuId: plant.rtus?.[0]?.rtuId ?? '',
    inverters: (plant.inverters ?? []).map((inverter) => ({
      inverterId: inverter.inverterId ?? '', // 발전소 유일 — UI 선택 식별자(instanceId 는 RTU 스코프라 중복).
      instanceId: inverter.instanceId ?? '',
      inverterName: inverter.name,
      imei: inverter.imei ?? '', // RTU imei = 2.0 device.deviceId(capabilities/mppt/string/history).
    })),
    plantStatus: plant.status ? (PLANT_STATUS_MAP[plant.status] ?? undefined) : undefined,
    plantLocation: plant.address
      ? [plant.address.statName, plant.address.sigunguName].filter(Boolean).join(' ')
      : undefined,
    plantCapacityKwp: plant.volume ?? undefined,
    plantLat: plant.address?.lat ?? null,
    plantLng: plant.address?.lng ?? null,
    plantEnergy: plant.energy ?? undefined,
    // 구독 상태 — 인버터 상세분석 진입 게이트(active 필수) 판정용. 없으면(미구독) null.
    subscription: plant.subscription
      ? { status: toSubscriptionStatus(plant.subscription.subscriptionStatus) }
      : null,
  }));
}

// app-legacy(v3) `GET /plants` → 발전 분석용 rich 발전소 목록(좌표·인버터 용량/출력). 스코프 셀렉터 lean
// `PlantWithDevices` 와 같은 응답이나 좌표·설비용량까지 보존. 인버터 status 는 발전소와 같은 코드 체계(101/201/…).
export function mapPlantsForAnalysis(raw: RawPlantsResponse): AnalysisPlant[] {
  return raw.plants.map((plant) => ({
    plantId: plant.plantId,
    plantName: plant.name,
    plantStatus: plant.status ? (PLANT_STATUS_MAP[plant.status] ?? undefined) : undefined,
    plantLocation: plant.address
      ? [plant.address.statName, plant.address.sigunguName].filter(Boolean).join(' ')
      : undefined,
    plantCapacityKwp: plant.volume ?? undefined,
    plantEnergy: plant.energy ?? undefined,
    plantLat: plant.address?.lat ?? null,
    plantLng: plant.address?.lng ?? null,
    contract: plant.contract
      ? {
          useCode: plant.contract.useCode ?? null,
          contractCode: plant.contract.contractCode ?? null,
          contractName: plant.contract.contractName ?? null,
          contractDetail: plant.contract.contractDetail ?? null,
        }
      : null,
    // 구독 상태 — 진입 게이트/집계 대상 판정용(소문자 도메인). 없으면(미구독) null.
    subscription: plant.subscription
      ? { status: toSubscriptionStatus(plant.subscription.subscriptionStatus) }
      : null,
    // 대시보드 실시간 지표(총 추정수익 합·최종수신) — 별도 realtime 엔드포인트 없이 /plants 임베드에서 파생.
    plantRevenue: plant.revenue ?? null,
    plantReceivedAt: plant.receivedAt ?? null,
    inverters: (plant.inverters ?? []).map((inverter) => ({
      statInverterId: Number(inverter.inverterId),
      inverterName: inverter.name,
      // 용량은 발전시간·현재출력 분모 — wire 결측(null) 시 0 폴백(합산 NaN → 'NaN시간' 방지). 매퍼가 null 방어 소유.
      moduleCapacity: inverter.moduleCapacity ?? 0,
      power: inverter.power ?? null,
      // 오늘 발전량 kWh(인버터 statistic.value) — 대시보드 오늘 총발전량·발전시간 분자(Σ). 결측 null.
      statValue: inverter.statistic?.value ?? null,
      inverterStatus: inverter.status
        ? (PLANT_STATUS_MAP[inverter.status] ?? undefined)
        : undefined,
    })),
  }));
}

// app-legacy(v3) `GET /plants/{id}/profile` → 정보 상세(기본/설치/계약 3섹션). 응답 없음(null) 방어 —
// 위젯이 null 을 에러 상태로. 계약(displayType)은 목록과 동일 4필드로 정규화(useCodeName·contractAt 드롭 —
// 발전용도명은 useCode 로 파생, 계약방식명은 contractName). contractDetail(JSON 문자열)은 파싱 없이 보존.
export function fromRawPlantProfile(raw: RawProfileResponse | null): PlantProfile | null {
  if (!raw) return null;
  const { plant, inverterProfile, contract } = raw.profile;
  return {
    plant: {
      plantId: plant.plantId,
      plantName: plant.plantIdName,
      plantEnergy: plant.energy,
      plantEnergyName: plant.energyName ?? null,
      plantLocation: plant.location,
      plantCapacityKwp: plant.capacity,
      plantInstalledAt: plant.installedAt ?? null,
    },
    inverters: (inverterProfile ?? []).map((inverter) => ({
      inverterId: inverter.inverterId,
      businessName: inverter.businessName ?? null,
      rtuCompanyName: inverter.rtuCompanyName ?? null,
      constructCompany: inverter.constructCompany ?? null,
      constructTel: inverter.constructTel ?? null,
    })),
    contract: contract
      ? {
          useCode: contract.useCode ?? null,
          contractCode: contract.contractCode ?? null,
          contractName: contract.contractName ?? null,
          contractDetail: contract.contractDetail ?? null,
        }
      : null,
  };
}
